import React, { useState } from 'react';
import type { Dossier, AppData } from '../types';
import { Button, Card, Input, Select } from './UI';

interface DossiersListProps {
  data: AppData;
  onDataChange: (data: AppData) => void;
}

export function DossiersList({ data, onDataChange }: DossiersListProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Dossier>>({});

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      id: `d${Date.now()}`,
      ref: '',
      client: '',
      type: 'Civil',
      statut: 'En cours',
      desc: '',
      honoraires: 0,
      ouverture: new Date().toISOString().split('T')[0],
      modeFacturation: 'horaire',
      tauxHoraire: 180,
      statutPaiement: 'En attente',
      montantRegle: 0,
    });
  };

  const handleEdit = (dossier: Dossier) => {
    setEditingId(dossier.id);
    setFormData(dossier);
  };

  const handleSave = () => {
    if (!formData.ref || !formData.client) {
      alert('Veuillez remplir les champs requis');
      return;
    }

    const newData = { ...data };
    if (isCreating) {
      newData.dossiers.push(formData as Dossier);
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.id!,
        action: 'Dossier créé',
        detail: `${formData.ref} — ${formData.client}`,
        ts: new Date().toISOString(),
      });
    } else {
      newData.dossiers = newData.dossiers.map((d) =>
        d.id === editingId ? (formData as Dossier) : d
      );
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.id!,
        action: 'Dossier modifié',
        detail: `${formData.ref} — ${formData.client}`,
        ts: new Date().toISOString(),
      });
    }

    onDataChange(newData);
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  const handleDelete = (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce dossier ?')) return;

    const dossier = data.dossiers.find((d) => d.id === id);
    const newData = { ...data };
    newData.dossiers = newData.dossiers.filter((d) => d.id !== id);
    newData.journal.push({
      id: `j${Date.now()}`,
      dossier: id,
      action: 'Dossier supprimé',
      detail: `${dossier?.ref} — ${dossier?.client}`,
      ts: new Date().toISOString(),
    });

    onDataChange(newData);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  if (isCreating || editingId) {
    return (
      <div>
        <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>
          {isCreating ? 'Créer un dossier' : 'Éditer le dossier'}
        </h1>

        <Card style={{ maxWidth: 600 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                Référence *
              </label>
              <Input
                type="text"
                value={formData.ref || ''}
                onChange={(e) => setFormData({ ...formData, ref: e.target.value })}
                placeholder="2024-001"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                Client *
              </label>
              <Input
                type="text"
                value={formData.client || ''}
                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                placeholder="Nom du client"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>Type</label>
              <Select
                value={formData.type || 'Civil'}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              >
                {['Civil', 'Pénal', 'Commercial', 'Social', 'Famille', 'Immobilier', 'Administratif', 'Autre'].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>Statut</label>
              <Select
                value={formData.statut || 'En cours'}
                onChange={(e) => setFormData({ ...formData, statut: e.target.value as any })}
              >
                {['En cours', 'En attente', 'Plaidé', 'Archivé'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                Description
              </label>
              <textarea
                value={formData.desc || ''}
                onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                placeholder="Description du dossier"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: 'var(--border-radius-md)',
                  border: '1px solid var(--color-border-secondary)',
                  fontSize: 13,
                  minHeight: 80,
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                  Date d'ouverture
                </label>
                <Input
                  type="date"
                  value={formData.ouverture || ''}
                  onChange={(e) => setFormData({ ...formData, ouverture: e.target.value })}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                  Mode de facturation
                </label>
                <Select
                  value={formData.modeFacturation || 'horaire'}
                  onChange={(e) => setFormData({ ...formData, modeFacturation: e.target.value as any })}
                >
                  <option value="horaire">Horaire</option>
                  <option value="forfait">Forfait</option>
                </Select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                  {formData.modeFacturation === 'horaire' ? 'Taux horaire (€)' : 'Honoraires forfaitaires (€)'}
                </label>
                <Input
                  type="number"
                  value={formData.modeFacturation === 'horaire' ? (formData.tauxHoraire || 180) : (formData.honoraires || 0)}
                  onChange={(e) =>
                    formData.modeFacturation === 'horaire'
                      ? setFormData({ ...formData, tauxHoraire: parseInt(e.target.value) || 0 })
                      : setFormData({ ...formData, honoraires: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: '0.25rem' }}>
                  Montant réglé (€)
                </label>
                <Input
                  type="number"
                  value={formData.montantRegle || 0}
                  onChange={(e) => setFormData({ ...formData, montantRegle: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <Button onClick={handleSave} variant="primary">
                {isCreating ? 'Créer' : 'Enregistrer'}
              </Button>
              <Button onClick={handleCancel} variant="secondary">
                Annuler
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Dossiers</h1>
        <Button onClick={handleCreate}>+ Créer un dossier</Button>
      </div>

      {data.dossiers.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--color-text-secondary)' }}>Aucun dossier pour le moment</p>
          <Button onClick={handleCreate} style={{ marginTop: '1rem' }}>
            Créer le premier dossier
          </Button>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {data.dossiers.map((dossier) => (
            <Card key={dossier.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <p style={{ margin: '0 0 0.25rem', fontSize: 14, fontWeight: 600 }}>
                    {dossier.ref} — {dossier.client}
                  </p>
                  <p style={{ margin: '0 0 0.5rem', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {dossier.type} • {dossier.statut}
                  </p>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    {dossier.desc}
                  </p>
                  <div style={{ marginTop: '0.5rem', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                    Honoraires: {dossier.honoraires}€ | Réglé: {dossier.montantRegle}€
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <Button onClick={() => handleEdit(dossier)} size="sm" variant="secondary">
                    Éditer
                  </Button>
                  <Button onClick={() => handleDelete(dossier.id)} size="sm" variant="secondary">
                    Supprimer
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
